/**
 * Book Carousel & Real-World 3D Page Curl Engine
 * Custom Elements: <book-carousel> & <book-flipper>
 *
 * Implements physically-accurate cylindrical paper curl mechanics,
 * seamless continuation from hover corner peel to full diagonal turn,
 * 3D cylinder lighting shaders, left rolled scroll (卷轴) accumulation,
 * depth of field, paper thickness, stacked fore-edge pages,
 * and responsive touch/drag gesture handling.
 */

(function () {
  'use strict';

  // Smooth cubic easing for physical page turning
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Smoothstep interpolation for natural phase delay between bottom and top corners
  function smoothStep(edge0, edge1, x) {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  }

  // ==========================================================================
  // Component: <book-flipper> (Real-World 3D Cylindrical Page Curl Engine)
  // ==========================================================================

  class BookFlipper extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isAnimating = false;
      this.images = [];
      this.imageElements = [];
      this.imagesLoaded = false;
      this.suppressClick = false;

      // Unified 3D Curl Progress (0 = flat, 0.15 = default resting corner peel, 0.20 = hover peel, 1.0 = turned under scroll)
      this.defaultPeel = 0.15;
      this.hoverPeel = 0.20;
      this.curlProgress = this.defaultPeel;
      this.hoverTarget = this.defaultPeel;
      this.hoverRaf = null;
      this.timerId = null;

      this._onResize = this.onResize.bind(this);
    }

    getIdleCurl() {
      if (this.images.length <= 1 || this.currentIndex >= this.images.length - 1) {
        return 0;
      }
      return this.defaultPeel;
    }

    connectedCallback() {
      this.initData();
      if (this.images.length === 0) return;

      this.curlProgress = this.getIdleCurl();
      this.hoverTarget = this.curlProgress;

      this.initDOMElements();
      this.preloadImages();
      this.initCanvas();
      this.bindEvents();
      this.updateStateUI();

      window.addEventListener('resize', this._onResize, { passive: true });
      if (typeof ResizeObserver !== 'undefined' && this.viewport) {
        this._resizeObserver = new ResizeObserver(() => {
          this.updateCanvasDimensions();
        });
        this._resizeObserver.observe(this.viewport);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      if (this.timerId) clearInterval(this.timerId);
      if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);
    }

    initData() {
      const dataEl = this.querySelector('.book-flipper__data');
      if (dataEl) {
        try {
          this.images = JSON.parse(dataEl.textContent.trim()).filter(Boolean);
        } catch (e) {
          console.warn('BookFlipper: failed to parse image data', e);
          this.images = [];
        }
      }

      if (this.images.length <= 1) {
        this.classList.add('is-single-image');
      }
    }

    initDOMElements() {
      this.viewport = this.querySelector('.book-flipper__viewport');
      this.scrollZone = this.querySelector('.book-flipper__scroll-zone');
      this.stage = this.querySelector('.book-flipper__stage');
      this.counterCurrent = this.querySelector('.book-flipper__counter-current');
      this.counterTotal = this.querySelector('.book-flipper__counter-total');
    }

    initCanvas() {
      if (!this.viewport) return;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'book-flipper__canvas';
      this.ctx = this.canvas.getContext('2d');
      this.viewport.prepend(this.canvas);
      this.updateCanvasDimensions();
    }

    updateCanvasDimensions() {
      if (!this.canvas) return;
      const rect = this.viewport.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      this.width = rect.width || 380;
      this.height = rect.height || 285;

      this.canvas.width = Math.round(this.width * dpr);
      this.canvas.height = Math.round(this.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.drawIdleState();
    }

    preloadImages() {
      let loaded = 0;
      const total = this.images.length;
      this.imageElements = this.images.map((src) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          loaded++;
          if (loaded === total) {
            this.imagesLoaded = true;
          }
          if (!this.isAnimating) {
            this.drawIdleState();
          }
        };
        img.src = src;
        return img;
      });
    }

    bindEvents() {
      if (this.images.length <= 1) return;

      // Click right page stage -> 3D flip forward; clicking left margin -> 3D flip backward
      if (this.stage) {
        this.stage.addEventListener('click', (e) => {
          if (this.suppressClick) return;
          const rect = this.stage.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          // If clicking on the left 35% of the book page and turned pages exist, turn back to previous page!
          if (clickX < Math.max(85, rect.width * 0.35) && this.currentIndex > 0) {
            this.prevPage();
            return;
          }
          this.nextPage();
        });

        // Hover Corner Peel: intensifies default corner curl when hovering over book page
        this.stage.addEventListener('pointermove', (e) => {
          const idleCurl = this.getIdleCurl();
          if (this.isAnimating || idleCurl === 0) {
            if (this.hoverTarget !== 0) this.setHoverTarget(0);
            return;
          }
          const rect = this.stage.getBoundingClientRect();
          const relX = e.clientX - rect.left;
          const relY = e.clientY - rect.top;
          if (relX > rect.width * 0.35 && relY > rect.height * 0.25) {
            if (this.hoverTarget !== this.hoverPeel) this.setHoverTarget(this.hoverPeel);
          } else {
            if (this.hoverTarget !== idleCurl) this.setHoverTarget(idleCurl);
          }
        });

        this.stage.addEventListener('pointerleave', () => {
          const idleCurl = this.getIdleCurl();
          if (!this.isAnimating && this.hoverTarget !== idleCurl) {
            this.setHoverTarget(idleCurl);
          }
        });

        this.stage.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.nextPage();
          }
        });
      }

      // Click left curled scroll -> 3D unroll backward
      if (this.scrollZone) {
        this.scrollZone.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this.suppressClick) return;
          this.prevPage();
        });

        this.scrollZone.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.prevPage();
          }
        });
      }
    }

    setHoverTarget(target) {
      this.hoverTarget = target;
      if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);

      const loop = () => {
        if (this.isAnimating) return;
        const diff = this.hoverTarget - this.curlProgress;
        if (Math.abs(diff) < 0.003) {
          this.curlProgress = this.hoverTarget;
          if (this.curlProgress <= 0.001) {
            this.drawIdleState();
          } else {
            this.renderForwardCurl(this.curlProgress, this.currentIndex, this.currentIndex + 1);
          }
          return;
        }
        this.curlProgress += diff * 0.22;
        if (this.curlProgress <= 0.002 && this.hoverTarget === 0) {
          this.curlProgress = 0;
          this.drawIdleState();
          return;
        }
        this.renderForwardCurl(this.curlProgress, this.currentIndex, this.currentIndex + 1);
        this.hoverRaf = requestAnimationFrame(loop);
      };
      this.hoverRaf = requestAnimationFrame(loop);
    }

    updateStateUI() {
      const total = this.images.length;
      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.currentIndex + 1;
      }
      if (this.counterTotal) {
        this.counterTotal.textContent = total;
      }

      this.style.setProperty('--turned-count', this.currentIndex);

      // Strictly enforce physical world boundaries
      if (this.currentIndex === 0) {
        this.classList.add('is-first-page');
        this.classList.remove('is-last-page');
      } else if (this.currentIndex >= total - 1) {
        this.classList.add('is-last-page');
        this.classList.remove('is-first-page');
      } else {
        this.classList.remove('is-first-page', 'is-last-page');
      }

      // Left scroll accessibility attributes
      if (this.currentIndex > 0) {
        this.classList.add('has-turned-pages');
        if (this.scrollZone) {
          this.scrollZone.setAttribute('tabindex', '0');
          this.scrollZone.setAttribute('aria-hidden', 'false');
          this.scrollZone.setAttribute('aria-label', `Return to page ${this.currentIndex}`);
        }
      } else {
        this.classList.remove('has-turned-pages');
        if (this.scrollZone) {
          this.scrollZone.setAttribute('tabindex', '-1');
          this.scrollZone.setAttribute('aria-hidden', 'true');
        }
      }

      if (this.stage) {
        if (this.currentIndex >= total - 1) {
          this.stage.setAttribute('aria-label', `Page ${this.currentIndex + 1} of ${total}. Final page.`);
        } else {
          this.stage.setAttribute('aria-label', `Page ${this.currentIndex + 1} of ${total}. Click to flip forward.`);
        }
      }

      this.drawIdleState();
    }

    // ------------------------------------------------------------------------
    // Page Turn Controls: Strictly Non-Looping, Continuing from Hover
    // ------------------------------------------------------------------------

    nextPage() {
      if (this.isAnimating || this.images.length <= 1) return;

      const total = this.images.length;
      // Strictly NO looping — book has reached the end
      if (this.currentIndex >= total - 1) {
        this.bounceFeedback();
        return;
      }
      if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);

      this.isAnimating = true;
      this.classList.add('is-flipping', 'is-flipping-forward');

      const from = this.currentIndex;
      const to = this.currentIndex + 1;
      const startP = this.curlProgress;
      const duration = Math.max(340, (1 - startP) * 650);
      const startTime = performance.now();

      if (this.timerId) clearInterval(this.timerId);
      this.timerId = setInterval(() => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / duration);
        const p = startP + (1 - startP) * easeInOutCubic(t);
        this.curlProgress = p;

        this.renderForwardCurl(p, from, to);

        if (t >= 1) {
          clearInterval(this.timerId);
          this.timerId = null;
          this.currentIndex = to;
          const nextIdle = this.getIdleCurl();
          this.curlProgress = nextIdle;
          this.hoverTarget = nextIdle;
          this.classList.remove('is-flipping', 'is-flipping-forward', 'is-flipping-backward');
          this.updateStateUI();
          this.isAnimating = false;
        }
      }, 16);
    }

    prevPage() {
      if (this.isAnimating || this.currentIndex <= 0) return;
      if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);

      this.isAnimating = true;
      this.classList.add('is-flipping', 'is-flipping-backward');

      const from = this.currentIndex;
      const to = this.currentIndex - 1;
      const duration = 650;
      const startTime = performance.now();

      if (this.timerId) clearInterval(this.timerId);
      this.timerId = setInterval(() => {
        const elapsed = performance.now() - startTime;
        const u = Math.min(1, elapsed / duration);
        const p = easeInOutCubic(u);
        this.curlProgress = p;

        this.renderBackwardCurl(p, from, to);

        if (u >= 1) {
          clearInterval(this.timerId);
          this.timerId = null;
          this.currentIndex = to;
          const nextIdle = this.getIdleCurl();
          this.curlProgress = nextIdle;
          this.hoverTarget = nextIdle;
          this.classList.remove('is-flipping', 'is-flipping-forward', 'is-flipping-backward');
          this.updateStateUI();
          this.isAnimating = false;
        }
      }, 16);
    }

    bounceFeedback() {
      // Elastic 3D curl resistance animation (book has reached the end)
      const start = performance.now();
      const loop = (now) => {
        const elapsed = now - start;
        if (elapsed > 350) {
          this.curlProgress = 0;
          this.drawIdleState();
          return;
        }
        const p = Math.sin((elapsed / 350) * Math.PI * 2) * Math.exp(-elapsed / 120);
        this.curlProgress = Math.max(0, p * 0.12);
        this.renderForwardCurl(this.curlProgress, this.currentIndex, this.currentIndex);
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    }

    // ========================================================================
    // Physical 3D Cylindrical Page Curl Renderer
    // ========================================================================

    getScrollWidth(pageIndex) {
      if (pageIndex <= 0) return 0;
      // Natural, restrained paper curl: slender, subtle paper roll (20px to 28px)
      return Math.min(28, 18 + Math.min(pageIndex, 5) * 2);
    }

    getRightStackWidth(remainingPages) {
      if (remainingPages <= 0) return 2;
      return Math.min(14, 3 + remainingPages * 2.5);
    }

    drawBottomBookThickness(ctx, pageX, w, h) {
      const thickH = 10;
      const y0 = h - thickH;
      ctx.save();

      // Desk drop shadow under the book bottom block
      const deskShadow = ctx.createLinearGradient(0, y0, 0, h + 6);
      deskShadow.addColorStop(0, 'rgba(18, 14, 10, 0.28)');
      deskShadow.addColorStop(0.5, 'rgba(18, 14, 10, 0.1)');
      deskShadow.addColorStop(1, 'rgba(18, 14, 10, 0)');
      ctx.fillStyle = deskShadow;
      ctx.fillRect(pageX + 2, y0, w - pageX - 2, thickH + 6);

      // Cream layered paper block bevel
      const blockGrad = ctx.createLinearGradient(0, y0, 0, h);
      blockGrad.addColorStop(0, '#ece6d8');
      blockGrad.addColorStop(0.3, '#fbf7ee');
      blockGrad.addColorStop(0.7, '#e4dccb');
      blockGrad.addColorStop(1, '#cbbeaa');
      ctx.fillStyle = blockGrad;
      ctx.fillRect(pageX, y0, w - pageX, thickH);

      // Horizontal stacked sheet lines (thick book pages resting on desk)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 1;
      for (let i = 1; i <= 3; i++) {
        const yLine = y0 + i * 2.2;
        ctx.beginPath();
        ctx.moveTo(pageX + 2, yLine);
        ctx.lineTo(w - 2, yLine);
        ctx.stroke();
      }

      ctx.restore();
    }

    drawIdleState() {
      if (!this.ctx || this.isAnimating) return;
      this.renderForwardCurl(this.curlProgress, this.currentIndex, this.currentIndex + 1);
    }

    // ------------------------------------------------------------------------
    // Forward Page Turn: Right to Left
    // Seamlessly connects with the hover corner curl:
    // Starts at bottom-right corner, pulls top-right diagonally, arches high in 3D,
    // and folds underneath the left scroll stack.
    // ------------------------------------------------------------------------
    renderForwardCurl(p, from, to) {
      const ctx = this.ctx;
      const w = this.width;
      const h = this.height;
      if (!ctx || !w || !h) return;

      ctx.clearRect(0, 0, w, h);

      const toRemainingPages = Math.max(0, this.images.length - 1 - to);
      const stackW = this.getRightStackWidth(toRemainingPages);

      const fromScrollW = this.getScrollWidth(from);
      const toScrollW = this.getScrollWidth(to);
      const currentScrollW = fromScrollW + (toScrollW - fromScrollW) * p;

      const pageX = currentScrollW;
      const pageW = w - currentScrollW - stackW;
      const pageH = h - 10;
      const pageRight = pageX + pageW;

      // 0. Bottom paper block thickness & right stacked pages
      this.drawBottomBookThickness(ctx, pageX, w, h);
      this.drawStackedPaperEdge(ctx, w, pageH, toRemainingPages, stackW);

      const currentImg = this.imageElements[from];
      const nextImg = this.imageElements[to];

      // Left scroll cylinder
      if (currentScrollW > 0.5) {
        this.drawSpineCrease(ctx, currentScrollW, pageH);
        this.drawLeftScrollCylinder(ctx, currentScrollW, pageH, from);
      }

      // STATE A: Completely Flat Idle Page
      if (p <= 0.001 || !nextImg) {
        this.drawCoverImage(ctx, currentImg, pageX, 0, pageW, pageH);
        return;
      }

      // STATE B: Corner Peel Phase (p <= 0.22, including hover corner!)
      if (p <= 0.22) {
        const pPeel = p / 0.22;
        const C = Math.min(pageW * 0.48, 86) * pPeel;

        const pBot = { x: pageRight - C * 1.05, y: pageH };
        const pRight = { x: pageRight, y: pageH - C * 0.95 };
        const apexX = pageRight - C * 0.88;
        const apexY = pageH - C * 0.96;

        // 1. Draw flat page everywhere except vacated corner
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pageX, 0);
        ctx.lineTo(pageRight, 0);
        ctx.lineTo(pRight.x, pRight.y);
        ctx.lineTo(pBot.x, pBot.y);
        ctx.lineTo(pageX, pageH);
        ctx.closePath();
        ctx.clip();
        this.drawCoverImage(ctx, currentImg, pageX, 0, pageW, pageH);
        ctx.restore();

        // 2. Draw revealed next page in vacated corner with high-contrast depth shadow
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pBot.x, pBot.y);
        ctx.lineTo(pageRight, pageH);
        ctx.lineTo(pRight.x, pRight.y);
        ctx.closePath();
        ctx.clip();
        this.drawCoverImage(ctx, nextImg, pageX, 0, pageW, pageH);

        // Deep crease shadow under the curled flap so the white corner stands out on light/cream pages
        const foldMidX = (pBot.x + pRight.x) / 2;
        const foldMidY = (pBot.y + pRight.y) / 2;
        const creaseShadow = ctx.createLinearGradient(
          foldMidX, foldMidY,
          pageRight, pageH
        );
        creaseShadow.addColorStop(0, 'rgba(18, 15, 12, 0.85)');
        creaseShadow.addColorStop(0.55, 'rgba(18, 15, 12, 0.68)');
        creaseShadow.addColorStop(1, 'rgba(18, 15, 12, 0.48)');
        ctx.fillStyle = creaseShadow;
        ctx.fill();
        ctx.restore();

        // 3. Drop Shadow cast by folded corner flap onto current page (to upper-left)
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pBot.x - 4, pBot.y);
        ctx.lineTo(apexX - 14, apexY - 14);
        ctx.lineTo(pRight.x, pRight.y - 4);
        ctx.lineTo(pRight.x, pRight.y);
        ctx.lineTo(pBot.x, pBot.y);
        ctx.closePath();
        const flapShadow = ctx.createRadialGradient(
          apexX, apexY, 0,
          apexX, apexY, C * 0.9
        );
        flapShadow.addColorStop(0, 'rgba(15, 12, 10, 0.42)');
        flapShadow.addColorStop(0.5, 'rgba(15, 12, 10, 0.16)');
        flapShadow.addColorStop(1, 'rgba(15, 12, 10, 0)');
        ctx.fillStyle = flapShadow;
        ctx.fill();
        ctx.restore();

        // 4. Folded Paper Corner Flap (Curled up and over, pointing inwards)
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pBot.x, pBot.y);
        ctx.quadraticCurveTo(
          (pBot.x + apexX) / 2 - 4,
          (pBot.y + apexY) / 2 + 2,
          apexX,
          apexY
        );
        ctx.quadraticCurveTo(
          (apexX + pRight.x) / 2 + 2,
          (apexY + pRight.y) / 2 - 4,
          pRight.x,
          pRight.y
        );
        ctx.quadraticCurveTo(
          (pBot.x + pRight.x) / 2 - 6,
          (pBot.y + pRight.y) / 2 - 6,
          pBot.x,
          pBot.y
        );
        ctx.closePath();

        const flapGrad = ctx.createLinearGradient(
          (pBot.x + pRight.x) / 2,
          (pBot.y + pRight.y) / 2,
          apexX,
          apexY
        );
        flapGrad.addColorStop(0, 'rgba(235, 228, 214, 0.98)');
        flapGrad.addColorStop(0.28, '#ffffff'); // 3D specular highlight along fold curve
        flapGrad.addColorStop(0.65, '#fbf8f0'); // Warm paper fiber body
        flapGrad.addColorStop(1, '#e8e0cc');
        ctx.fillStyle = flapGrad;
        ctx.fill();

        // Paper Cut Edge Thickness (White paper core + bevel shadow)
        ctx.beginPath();
        ctx.moveTo(pBot.x, pBot.y);
        ctx.quadraticCurveTo(
          (pBot.x + apexX) / 2 - 4,
          (pBot.y + apexY) / 2 + 2,
          apexX,
          apexY
        );
        ctx.quadraticCurveTo(
          (apexX + pRight.x) / 2 + 2,
          (apexY + pRight.y) / 2 - 4,
          pRight.x,
          pRight.y
        );
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.96)';
        ctx.lineWidth = 2.4;
        ctx.stroke();

        ctx.strokeStyle = 'rgba(35, 28, 20, 0.35)';
        ctx.lineWidth = 1.0;
        ctx.stroke();
        ctx.restore();
        return;
      }

      // STATE C: Continuous Full 3D Conical Diagonal Turn (p > 0.22, Photos 2 & 3)
      const pSweep = (p - 0.22) / 0.78;
      const pBot = Math.min(1, pSweep * 1.18);
      const pTop = smoothStep(0.12, 1.0, pSweep);

      const xBot = (pageRight - 82) + (pageX - (pageRight - 82)) * easeInOutCubic(pBot);
      const xTop = pageRight + (pageX - pageRight) * easeInOutCubic(pTop);

      const rBot = 24 + 22 * Math.sin(pBot * Math.PI);
      const rTop = 20 + 16 * Math.sin(pTop * Math.PI);

      // 1. Underlay Revealed Next Page
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(Math.max(pageX, xTop), 0);
      ctx.lineTo(pageRight, 0);
      ctx.lineTo(pageRight, pageH);
      ctx.lineTo(Math.max(pageX, xBot), pageH);
      ctx.closePath();
      ctx.clip();
      this.drawCoverImage(ctx, nextImg, pageX, 0, pageW, pageH);

      // Depth of Field Atmospheric Wash
      const dofWashAlpha = 0.12 * Math.sin(pSweep * Math.PI);
      if (dofWashAlpha > 0.01) {
        ctx.fillStyle = `rgba(18, 14, 10, ${dofWashAlpha})`;
        ctx.fillRect(pageX, 0, pageW, pageH);
      }

      // Moving angled drop shadow
      const shadowW = Math.max(rBot, rTop) * 3.8;
      const minApex = Math.min(xTop, xBot);
      if (shadowW > 0 && minApex < pageRight) {
        const shadowGrad = ctx.createLinearGradient(minApex, 0, minApex + shadowW, 0);
        const peakAlpha = 0.68 * Math.sin(pSweep * Math.PI);
        shadowGrad.addColorStop(0, `rgba(10, 8, 5, ${peakAlpha})`);
        shadowGrad.addColorStop(0.18, `rgba(12, 9, 6, ${peakAlpha * 0.55})`);
        shadowGrad.addColorStop(0.48, `rgba(15, 12, 8, ${peakAlpha * 0.22})`);
        shadowGrad.addColorStop(1, 'rgba(15, 12, 8, 0)');
        ctx.fillStyle = shadowGrad;
        ctx.fillRect(minApex, 0, shadowW, pageH);
      }
      ctx.restore();

      // 2. Flat Unpeeled Region of Current Page
      if (xTop > pageX || xBot > pageX) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pageX, 0);
        ctx.lineTo(Math.max(pageX, xTop), 0);
        ctx.lineTo(Math.max(pageX, xBot), pageH);
        ctx.lineTo(pageX, pageH);
        ctx.closePath();
        ctx.clip();
        this.drawCoverImage(ctx, currentImg, pageX, 0, pageW, pageH);

        const creaseW = 24;
        const creaseGrad = ctx.createLinearGradient(
          Math.max(pageX, xBot) - creaseW, 0,
          Math.max(pageX, xBot), 0
        );
        creaseGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
        creaseGrad.addColorStop(0.6, 'rgba(10, 8, 6, 0.15)');
        creaseGrad.addColorStop(1, 'rgba(10, 8, 6, 0.45)');
        ctx.fillStyle = creaseGrad;
        ctx.fillRect(pageX, 0, pageW, pageH);
        ctx.restore();
      }

      // 3. Physical Diagonal 3D Conical Paper Loop (Photos 2 & 3)
      if (currentImg && currentImg.complete) {
        const numSlices = 48;
        for (let i = 0; i < numSlices; i++) {
          const theta = (i + 0.5) / numSlices * Math.PI;

          const xt = xTop - rTop * Math.sin(theta);
          const wt = Math.max(0.75, (Math.PI * rTop / numSlices) * Math.abs(Math.cos(theta)));

          const xb = xBot - rBot * Math.sin(theta);
          const wb = Math.max(0.75, (Math.PI * rBot / numSlices) * Math.abs(Math.cos(theta)));

          if (Math.max(xt, xb) < pageX - 30 || Math.min(xt, xb) > pageRight + 20) continue;

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(xt, 0);
          ctx.lineTo(xt + wt + 0.35, 0);
          ctx.lineTo(xb + wb + 0.35, pageH);
          ctx.lineTo(xb, pageH);
          ctx.closePath();
          ctx.clip();

          if (theta <= Math.PI / 2) {
            // Front face of page rolling down
            this.drawCoverImage(ctx, currentImg, pageX, 0, pageW, pageH);

            const highlight = Math.pow(Math.sin(theta), 5);
            const ambient = Math.cos(theta);

            if (highlight > 0.04) {
              ctx.fillStyle = `rgba(255, 255, 255, ${highlight * 0.68})`;
              ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
            }
            if (ambient > 0.08) {
              ctx.fillStyle = `rgba(15, 10, 6, ${ambient * 0.32})`;
              ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
            }
          } else {
            // Back face of page arching up (Photos 2 & 3)
            ctx.fillStyle = '#faf6ed';
            ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);

            ctx.globalAlpha = 0.22;
            ctx.filter = 'blur(1px) grayscale(0.35)';
            ctx.save();
            ctx.translate((xTop + xBot), 0);
            ctx.scale(-1, 1);
            this.drawCoverImage(ctx, currentImg, pageX, 0, pageW, pageH);
            ctx.restore();
            ctx.globalAlpha = 1.0;
            ctx.filter = 'none';

            const backShadow = Math.sin(theta) * 0.38;
            ctx.fillStyle = `rgba(25, 20, 15, ${backShadow})`;
            ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
          }
          ctx.restore();
        }
      }

      // 4. Left Rolled Scroll: Arriving sheet tucks and folds underneath
      if (currentScrollW > 1) {
        if (pSweep > 0.65) {
          const tuckAlpha = Math.min(0.42, (pSweep - 0.65) * 1.2);
          const tuckGrad = ctx.createLinearGradient(currentScrollW - 12, 0, currentScrollW + 4, 0);
          tuckGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
          tuckGrad.addColorStop(1, `rgba(18, 14, 10, ${tuckAlpha})`);
          ctx.fillStyle = tuckGrad;
          ctx.fillRect(currentScrollW - 12, 0, 16, pageH);
        }

        let cushion = 0;
        if (pSweep > 0.75) {
          cushion = Math.sin(((pSweep - 0.75) / 0.25) * Math.PI) * 1.8;
        }
        const effectiveScrollW = currentScrollW + cushion;

        this.drawSpineCrease(ctx, effectiveScrollW, pageH);
        this.drawLeftScrollCylinder(ctx, effectiveScrollW, pageH, Math.max(1, to));
      }
    }

    // ------------------------------------------------------------------------
    // Backward Page Turn: Left to Right
    // Exactly the opposite: unrolls from bottom-left of the scroll first,
    // sweeping diagonally across, and bottom-right corner settles down flat last!
    // ------------------------------------------------------------------------
    renderBackwardCurl(p, from, to) {
      const ctx = this.ctx;
      const w = this.width;
      const h = this.height;
      if (!ctx || !w || !h) return;

      ctx.clearRect(0, 0, w, h);

      const toRemainingPages = Math.max(0, this.images.length - 1 - to);
      const stackW = this.getRightStackWidth(toRemainingPages);

      const fromScrollW = this.getScrollWidth(from);
      const toScrollW = this.getScrollWidth(to);
      const currentScrollW = fromScrollW + (toScrollW - fromScrollW) * p;

      const pageX = currentScrollW;
      const pageW = w - currentScrollW - stackW;
      const pageH = h - 10;
      const pageRight = pageX + pageW;

      // 0. Bottom paper block thickness & right stacked pages
      this.drawBottomBookThickness(ctx, pageX, w, h);
      this.drawStackedPaperEdge(ctx, w, pageH, toRemainingPages, stackW);

      const deskImg = this.imageElements[from];
      const unrollingImg = this.imageElements[to];

      // Physical Phase Delay: Bottom leads unroll left to right; top trails behind!
      const pBot = Math.min(1, p * 1.25);
      const pTop = smoothStep(0.12, 1.0, p);

      const xBot = pageX + pageW * easeInOutCubic(pBot);
      const xTop = pageX + pageW * easeInOutCubic(pTop);

      const rBot = 24 + 22 * Math.sin(pBot * Math.PI);
      const rTop = 20 + 16 * Math.sin(pTop * Math.PI);

      // 1. Underlay Page on Desk (Page 'from') being covered
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(Math.max(pageX, xTop), 0);
      ctx.lineTo(pageRight, 0);
      ctx.lineTo(pageRight, pageH);
      ctx.lineTo(Math.max(pageX, xBot), pageH);
      ctx.closePath();
      ctx.clip();
      this.drawCoverImage(ctx, deskImg, pageX, 0, pageW, pageH);

      // Moving drop shadow
      const shadowW = Math.max(rBot, rTop) * 3.8;
      const minApex = Math.min(xTop, xBot);
      if (shadowW > 0 && minApex < pageRight) {
        const shadowGrad = ctx.createLinearGradient(minApex, 0, minApex + shadowW, 0);
        const peakAlpha = 0.68 * Math.sin(p * Math.PI);
        shadowGrad.addColorStop(0, `rgba(10, 8, 5, ${peakAlpha})`);
        shadowGrad.addColorStop(0.18, `rgba(12, 9, 6, ${peakAlpha * 0.55})`);
        shadowGrad.addColorStop(0.48, `rgba(15, 12, 8, ${peakAlpha * 0.22})`);
        shadowGrad.addColorStop(1, 'rgba(15, 12, 8, 0)');
        ctx.fillStyle = shadowGrad;
        ctx.fillRect(minApex, 0, shadowW, pageH);
      }
      ctx.restore();

      // 2. Unrolled Flat Region of Page 'to'
      if (xTop > pageX || xBot > pageX) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pageX, 0);
        ctx.lineTo(Math.max(pageX, xTop), 0);
        ctx.lineTo(Math.max(pageX, xBot), pageH);
        ctx.lineTo(pageX, pageH);
        ctx.closePath();
        ctx.clip();
        this.drawCoverImage(ctx, unrollingImg, pageX, 0, pageW, pageH);

        const creaseW = 24;
        const creaseGrad = ctx.createLinearGradient(
          Math.max(pageX, xBot) - creaseW, 0,
          Math.max(pageX, xBot), 0
        );
        creaseGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
        creaseGrad.addColorStop(0.6, 'rgba(10, 8, 6, 0.15)');
        creaseGrad.addColorStop(1, 'rgba(10, 8, 6, 0.45)');
        ctx.fillStyle = creaseGrad;
        ctx.fillRect(pageX, 0, pageW, pageH);
        ctx.restore();
      }

      // 3. Physical Diagonal 3D Conical Paper Loop of Page 'to'
      if (unrollingImg && unrollingImg.complete) {
        const numSlices = 48;
        for (let i = 0; i < numSlices; i++) {
          const theta = (i + 0.5) / numSlices * Math.PI;

          const xt = xTop - rTop * Math.sin(theta);
          const wt = Math.max(0.75, (Math.PI * rTop / numSlices) * Math.abs(Math.cos(theta)));

          const xb = xBot - rBot * Math.sin(theta);
          const wb = Math.max(0.75, (Math.PI * rBot / numSlices) * Math.abs(Math.cos(theta)));

          if (Math.max(xt, xb) < pageX - 30 || Math.min(xt, xb) > pageRight + 20) continue;

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(xt, 0);
          ctx.lineTo(xt + wt + 0.35, 0);
          ctx.lineTo(xb + wb + 0.35, pageH);
          ctx.lineTo(xb, pageH);
          ctx.closePath();
          ctx.clip();

          if (theta <= Math.PI / 2) {
            // Front face rolling down onto the desk
            this.drawCoverImage(ctx, unrollingImg, pageX, 0, pageW, pageH);

            const highlight = Math.pow(Math.sin(theta), 5);
            const ambient = Math.cos(theta);

            if (highlight > 0.04) {
              ctx.fillStyle = `rgba(255, 255, 255, ${highlight * 0.65})`;
              ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
            }
            if (ambient > 0.08) {
              ctx.fillStyle = `rgba(15, 10, 6, ${ambient * 0.32})`;
              ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
            }
          } else {
            // Back face arching up from the left spine
            ctx.fillStyle = '#faf6ed';
            ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);

            ctx.globalAlpha = 0.22;
            ctx.filter = 'blur(1px) grayscale(0.35)';
            ctx.save();
            ctx.translate((xTop + xBot), 0);
            ctx.scale(-1, 1);
            this.drawCoverImage(ctx, unrollingImg, pageX, 0, pageW, pageH);
            ctx.restore();
            ctx.globalAlpha = 1.0;
            ctx.filter = 'none';

            const backShadow = Math.sin(theta) * 0.38;
            ctx.fillStyle = `rgba(25, 20, 15, ${backShadow})`;
            ctx.fillRect(Math.min(xt, xb), 0, Math.max(wt, wb) + 80, pageH);
          }
          ctx.restore();
        }
      }

      // 4. Left Scroll Cylinder (shrinking back to 'to' index)
      if (currentScrollW > 1) {
        this.drawSpineCrease(ctx, currentScrollW, pageH);
        this.drawLeftScrollCylinder(ctx, currentScrollW, pageH, Math.max(0, to));
      }
    }

    drawCoverImage(ctx, img, destX, destY, destW, destH) {
      if (!img || !img.complete || img.naturalWidth === 0) {
        ctx.fillStyle = '#f5f3ec';
        ctx.fillRect(destX, destY, destW, destH);
        return;
      }

      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      const targetRatio = destW / destH;
      const imgRatio = imgW / imgH;

      let sW, sH, sX, sY;
      if (imgRatio > targetRatio) {
        sH = imgH;
        sW = imgH * targetRatio;
        sX = (imgW - sW) / 2;
        sY = 0;
      } else {
        sW = imgW;
        sH = imgW / targetRatio;
        sX = 0;
        sY = (imgH - sH) / 2;
      }

      ctx.drawImage(img, sX, sY, sW, sH, destX, destY, destW, destH);
    }

    drawLeftScrollCylinder(ctx, scrollW, h, turnedCount) {
      if (scrollW <= 1) return;
      ctx.save();

      // Soft paper drop shadow cast by the curl onto the book page
      const shadowGrad = ctx.createLinearGradient(scrollW, 0, scrollW + 16, 0);
      shadowGrad.addColorStop(0, 'rgba(25, 20, 16, 0.3)');
      shadowGrad.addColorStop(0.35, 'rgba(25, 20, 16, 0.1)');
      shadowGrad.addColorStop(1, 'rgba(25, 20, 16, 0)');
      ctx.fillStyle = shadowGrad;
      ctx.fillRect(scrollW, 0, 16, h);

      // Natural Book Paper Curl Lighting: Organic, soft, tactile parchment
      const cylGrad = ctx.createLinearGradient(0, 0, scrollW, 0);
      cylGrad.addColorStop(0, 'rgba(35, 28, 22, 0.45)');
      cylGrad.addColorStop(0.18, 'rgba(180, 170, 158, 0.25)');
      cylGrad.addColorStop(0.36, 'rgba(255, 255, 255, 0.72)');
      cylGrad.addColorStop(0.6, '#f9f6ed');
      cylGrad.addColorStop(0.85, 'rgba(195, 185, 170, 0.42)');
      cylGrad.addColorStop(1, 'rgba(40, 32, 24, 0.55)');

      const r = Math.min(5, scrollW / 2);
      ctx.fillStyle = cylGrad;
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(0, 0, scrollW, h, [r, 0, 0, r]);
      } else {
        ctx.rect(0, 0, scrollW, h);
      }
      ctx.fill();

      // Top and bottom spiral paper coil rims
      this.drawScrollSpiralRims(ctx, scrollW, h);

      // Subtle fine longitudinal sheet lines indicating turned pages
      if (scrollW > 10) {
        const numLines = Math.min(4, turnedCount);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        for (let l = 1; l <= numLines; l++) {
          const lx = scrollW - l * 2.2;
          if (lx > 6) {
            ctx.beginPath();
            ctx.moveTo(lx, 5);
            ctx.lineTo(lx, h - 5);
            ctx.stroke();
          }
        }
      }

      ctx.restore();
    }

    drawScrollSpiralRims(ctx, scrollW, h) {
      if (scrollW < 6) return;
      const radiusX = Math.max(1, scrollW / 2 - 1.5);
      const radiusY = Math.max(1, Math.min(4.5, scrollW * 0.16));

      ctx.save();
      // Top oval cap tilted with camera angle
      ctx.beginPath();
      ctx.ellipse(scrollW / 2, 4.5, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#f5efe4';
      ctx.fill();
      ctx.strokeStyle = 'rgba(30, 24, 18, 0.22)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Fine concentric paper coil rings
      const rings = Math.min(3, Math.floor(scrollW / 7));
      for (let r = 1; r <= rings; r++) {
        const factor = (rings - r + 0.4) / rings;
        ctx.beginPath();
        ctx.ellipse(
          scrollW / 2 - r * 0.4,
          4.5,
          Math.max(0.8, radiusX * factor),
          Math.max(0.6, radiusY * factor),
          0,
          0,
          Math.PI * 2
        );
        ctx.strokeStyle = `rgba(30, 24, 18, ${0.1 + r * 0.08})`;
        ctx.stroke();
      }

      // Bottom oval cap
      ctx.beginPath();
      ctx.ellipse(scrollW / 2, h - 4.5, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#f5efe4';
      ctx.fill();
      ctx.strokeStyle = 'rgba(30, 24, 18, 0.2)';
      ctx.stroke();
      ctx.restore();
    }

    drawSpineCrease(ctx, spineX, h) {
      ctx.save();
      const creaseGrad = ctx.createLinearGradient(spineX - 3, 0, spineX + 6, 0);
      creaseGrad.addColorStop(0, 'rgba(0, 0, 0, 0.35)');
      creaseGrad.addColorStop(0.3, 'rgba(0, 0, 0, 0.12)');
      creaseGrad.addColorStop(0.7, 'rgba(255, 255, 255, 0.2)');
      creaseGrad.addColorStop(1, 'rgba(0, 0, 0, 0.24)');
      ctx.fillStyle = creaseGrad;
      ctx.fillRect(spineX - 3, 0, 9, h);
      ctx.restore();
    }

    // ------------------------------------------------------------------------
    // Right Fore-Edge Stacked Paper Sheets (可以看到有几张的效果 + 纸张厚度)
    // ------------------------------------------------------------------------
    drawStackedPaperEdge(ctx, w, h, remainingPages, stackWidth) {
      if (remainingPages <= 0) {
        ctx.save();
        ctx.fillStyle = '#ded9cb';
        ctx.fillRect(w - 2, 2, 2, h - 4);
        ctx.strokeStyle = 'rgba(30, 24, 18, 0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(w - 1, 2);
        ctx.lineTo(w - 1, h - 2);
        ctx.stroke();
        ctx.restore();
        return;
      }

      ctx.save();
      const count = Math.min(6, remainingPages);

      // 1. Fore-edge paper block background (cream book deckle)
      const deckleGrad = ctx.createLinearGradient(w - stackWidth, 0, w, 0);
      deckleGrad.addColorStop(0, '#f8f5ee');
      deckleGrad.addColorStop(0.35, '#f1ebdf');
      deckleGrad.addColorStop(0.7, '#e6ded0');
      deckleGrad.addColorStop(1, '#d8d0bf');
      ctx.fillStyle = deckleGrad;
      ctx.fillRect(w - stackWidth, 2, stackWidth, h - 4);

      // 2. Individual stacked paper sheets (distinct visible paper layers)
      for (let i = 1; i <= count; i++) {
        const xSheet = w - stackWidth + (i / (count + 1)) * stackWidth;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xSheet, 2 + i * 0.4);
        ctx.lineTo(xSheet, h - 2 - i * 0.4);
        ctx.stroke();

        ctx.strokeStyle = `rgba(35, 28, 20, ${0.14 + i * 0.04})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xSheet + 1, 2 + i * 0.4);
        ctx.lineTo(xSheet + 1, h - 2 - i * 0.4);
        ctx.stroke();
      }

      // Outer right boundary contact shadow
      ctx.strokeStyle = 'rgba(25, 20, 15, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(w - 0.5, 2);
      ctx.lineTo(w - 0.5, h - 2);
      ctx.stroke();

      ctx.restore();
    }

    onResize() {
      this.updateCanvasDimensions();
    }
  }

  // ==========================================================================
  // Component: <book-carousel> (Outer Slider & Unified Gesture Dispatcher)
  // ==========================================================================

  class BookCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isDragging = false;
      this.isPointerDown = false;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerCurrentX = 0;
      this.dragOffset = 0;
      this.suppressClickUntil = 0;
      this._onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.book-carousel__viewport');
      this.track = this.querySelector('.book-carousel__track');
      this.slides = Array.from(this.querySelectorAll('.book-carousel__slide'));
      this.btnPrev = this.querySelector('[data-action="prev"]');
      this.btnNext = this.querySelector('[data-action="next"]');
      this.dotsContainer = this.querySelector('.book-carousel__dots');

      if (this.slides.length === 0) return;

      this.readConfig();
      this.initDots();
      this.bindEvents();
      this.updateLayout();

      window.addEventListener('resize', this._onResize, { passive: true });

      // Shopify Editor live reload hooks
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (e) => {
          const index = this.slides.indexOf(e.target.closest('.book-carousel__slide'));
          if (index !== -1) this.goTo(index);
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    readConfig() {
      this.colsDesktop = parseInt(this.getAttribute('data-cols-desktop') || '3', 10);
      this.colsMobile = parseFloat(this.getAttribute('data-cols-mobile') || '1');
      this.loop = this.getAttribute('data-loop') === 'true'; // strictly false by default, no looping
    }

    isDesktopViewport() {
      return window.innerWidth >= 768;
    }

    getPerView() {
      if (this.isDesktopViewport()) return this.colsDesktop;
      return this.colsMobile;
    }

    getMaxIndex() {
      const perView = Math.floor(this.getPerView());
      return Math.max(0, this.slides.length - perView);
    }

    initDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      const count = this.getMaxIndex() + 1;
      if (count <= 1) {
        this.dotsContainer.style.display = 'none';
        return;
      }
      this.dotsContainer.style.display = 'flex';

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `book-carousel__dot${i === 0 ? ' is-active' : ''}`;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsContainer.appendChild(dot);
      }
    }

    bindEvents() {
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => this.prev());
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => this.next());
      }

      // Mobile touch drag events only (PC only moves via arrow buttons)
      if (this.viewport) {
        this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
        window.addEventListener('pointermove', this.onPointerMove.bind(this));
        window.addEventListener('pointerup', this.onPointerUp.bind(this));
        window.addEventListener('pointercancel', this.onPointerUp.bind(this));

        // Prevent native horizontal scroll offset when focusing inner elements
        this.viewport.addEventListener('scroll', () => {
          if (this.viewport.scrollLeft !== 0) {
            this.viewport.scrollLeft = 0;
          }
        });

        // Keyboard arrow keys
        this.viewport.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prev();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.next();
          }
        });
      }
    }

    onPointerDown(e) {
      // PC / Desktop or mouse input: only allow arrow navigation, never drag/slide outer carousel
      if (e.pointerType === 'mouse' || this.isDesktopViewport()) return;
      if (this.getMaxIndex() <= 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.pointerStartX = e.clientX;
      this.pointerStartY = e.clientY;
      this.dragOffset = 0;
      this.dragStartTime = performance.now();
    }

    onPointerMove(e) {
      if (!this.isPointerDown) return;
      if (e.pointerType === 'mouse' || this.isDesktopViewport()) {
        this.isPointerDown = false;
        return;
      }

      const deltaX = e.clientX - this.pointerStartX;
      const deltaY = e.clientY - this.pointerStartY;

      // If scrolling vertically, let native scroll handle it
      if (!this.isDragging && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
        this.isPointerDown = false;
        return;
      }

      // If horizontal movement crosses threshold, lock into drag
      if (!this.isDragging && Math.abs(deltaX) > 10) {
        this.isDragging = true;
        this.track.classList.add('is-dragging');
        this.tellChildrenSuppressClick(true);
      }

      if (this.isDragging) {
        const maxIndex = this.getMaxIndex();
        let effectiveDeltaX = deltaX;
        if (!this.loop) {
          if ((this.currentIndex <= 0 && deltaX > 0) || (this.currentIndex >= maxIndex && deltaX < 0)) {
            effectiveDeltaX = deltaX * 0.25;
          }
        }
        this.dragOffset = deltaX;
        const currentTranslate = this.getSlideTranslate(this.currentIndex);
        this.track.style.transform = `translate3d(${currentTranslate + effectiveDeltaX}px, 0, 0)`;
      }
    }

    onPointerUp() {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;

      if (this.isDragging) {
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
        this.suppressClickUntil = performance.now() + 150;
        setTimeout(() => this.tellChildrenSuppressClick(false), 160);

        const slideWidth = this.getSlideWidth();
        const threshold = slideWidth * 0.22;
        const velocity = Math.abs(this.dragOffset) / (performance.now() - this.dragStartTime);

        if (this.dragOffset < -threshold || (this.dragOffset < -15 && velocity > 0.35)) {
          this.next();
        } else if (this.dragOffset > threshold || (this.dragOffset > 15 && velocity > 0.35)) {
          this.prev();
        } else {
          this.updateLayout();
        }
      }
    }

    tellChildrenSuppressClick(suppress) {
      const flippers = this.querySelectorAll('book-flipper');
      flippers.forEach((flipper) => {
        flipper.suppressClick = suppress;
      });
    }

    getSlideWidth() {
      if (this.slides.length === 0) return 0;
      return this.slides[0].getBoundingClientRect().width;
    }

    getSlideGap() {
      const style = window.getComputedStyle(this.track);
      return parseFloat(style.columnGap || style.gap || 24);
    }

    getSlideTranslate(index) {
      const slideWidth = this.getSlideWidth();
      const gap = this.getSlideGap();
      return -(index * (slideWidth + gap));
    }

    updateLayout() {
      if (!this.track || this.slides.length === 0) return;
      const targetX = this.getSlideTranslate(this.currentIndex);
      this.track.style.transform = `translate3d(${targetX}px, 0, 0)`;
      this.updateControls();
    }

    updateControls() {
      const maxIndex = this.getMaxIndex();

      if (this.btnPrev) {
        this.btnPrev.disabled = !this.loop && this.currentIndex <= 0;
      }
      if (this.btnNext) {
        this.btnNext.disabled = !this.loop && this.currentIndex >= maxIndex;
      }

      if (this.dotsContainer) {
        const dots = Array.from(this.dotsContainer.children);
        dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === this.currentIndex);
        });
      }
    }

    prev() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else if (this.loop && maxIndex > 0) {
        this.currentIndex = maxIndex;
      }
      this.updateLayout();
    }

    next() {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
      } else if (this.loop && maxIndex > 0) {
        this.currentIndex = 0;
      }
      this.updateLayout();
    }

    goTo(index) {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      this.updateLayout();
    }

    onResize() {
      this.initDots();
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
      this.updateLayout();
    }
  }

  // ==========================================================================
  // Custom Elements Definition
  // ==========================================================================

  if (!customElements.get('book-flipper')) {
    customElements.define('book-flipper', BookFlipper);
  }

  if (!customElements.get('book-carousel')) {
    customElements.define('book-carousel', BookCarousel);
  }
})();
